import { Box, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useWatch } from 'react-hook-form';

import { Button } from '@/components/form/baseInputs/Button';
import { FormAwareButtonGroup } from '@/components/form/formAwareInputs/FormAwareButtonGroup';
import { FormAwareDatePickerInput } from '@/components/form/formAwareInputs/FormAwareDatePickerInput';
import { FormAwareSelectInput } from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal/ConfirmationModal';
import { Alert } from '@/components/notifications/Alert/Alert';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { FormProvider, useForm } from '@/components/react-hook-form';
import { useReportError } from '@/hooks/useReportError';
import {
  TenantKind,
  TenantProductTier,
  TenantUsageBucket,
} from '@/types/schema';
import { getEnvironment } from '@/utils/environmentUtils';
import { getTenant } from '@/utils/tenantUtils';

import { usageBucketOptions } from '../AdminTenantsPage.constants';
import {
  useTenantSettingsQuery,
  useUpdateTenantMutation,
} from '../graphql/AdminTenantsPage.generated';
import {
  TenantUpdateFormField,
  TenantUpdateFormShape,
} from './TenantSettingsForm.types';

function useTenantSettingsData() {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const { data, ...queryProps } = useTenantSettingsQuery({
    onError: (err) => {
      showFeedback('Failed to fetch tenant settings');
      reportError('could not fetch tenant settings for internal page', err);
    },
  });

  const {
    id: tenantId,
    usageBucket,
    kind,
    name,
    branding,
    addonAssetProviderIntegrationEnabled,
    tier,
    trialEndDate,
  } = data?.tenantInformation ?? {};

  return {
    data,
    tenantId,
    usageBucket,
    kind,
    name,
    branding,
    addonAssetProviderIntegrationEnabled,
    tier,
    trialEndDate: trialEndDate,
    ...queryProps,
  };
}

/**
 * @description TenantSettingsForm
 */
export function TenantSettingsForm() {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const [showDemotionWarning, setShowDemotionWarning] = useState(false);
  const [pendingFormData, setPendingFormData] =
    useState<TenantUpdateFormShape | null>(null);
  const {
    tenantId,
    usageBucket: initialUsageBucket,
    kind,
    name,
    branding,
    tier,
    addonAssetProviderIntegrationEnabled,
    trialEndDate,
  } = useTenantSettingsData();
  const environment = getEnvironment();
  const tenantSubdomain = getTenant();

  const formMethods = useForm<TenantUpdateFormShape>({
    defaultValues: {
      displayName: '',
      usageBucket: usageBucketOptions[0]?.value,
      productionLevel: TenantKind.NonProduction,
      addonAssetProviderIntegrationEnabled: false,
      tier: TenantProductTier.Pro,
      trialEndDate: null,
    },
  });
  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = formMethods;

  useEffect(() => {
    reset({
      displayName: name,
      usageBucket: initialUsageBucket,
      productionLevel: kind,
      addonAssetProviderIntegrationEnabled:
        addonAssetProviderIntegrationEnabled ?? false,
      tier: tier ?? TenantProductTier.Pro,
      trialEndDate: trialEndDate,
    });
  }, [
    kind,
    name,
    reset,
    initialUsageBucket,
    addonAssetProviderIntegrationEnabled,
    tier,
    trialEndDate,
  ]);

  const [updateTenant] = useUpdateTenantMutation({
    onError: (err) => {
      showFeedback('Failed to update tenant');
      reportError('could not update tenant', err);
    },
    onCompleted: () => {
      showFeedback('Tenant updated successfully', { variant: 'success' });
    },
  });

  async function onSubmit(formData: TenantUpdateFormShape) {
    if (!branding?.id || !tenantId) {
      throw new Error('Missing data for this tenant');
    }

    // Check if user is demoting from Pro to Go
    if (
      tier === TenantProductTier.Pro &&
      formData.tier === TenantProductTier.Go
    ) {
      setShowDemotionWarning(true);
      setPendingFormData(formData);
      return;
    }

    return updateTenant({
      variables: {
        tenantInput: {
          id: tenantId,
          update: {
            usageBucket: formData.usageBucket,
            kind: formData.productionLevel,
            addonAssetProviderIntegrationEnabled:
              formData.addonAssetProviderIntegrationEnabled,
            productTier: formData.tier,
            trialEndDate: formData.trialEndDate,
            clearTrialEndDate: formData.trialEndDate === null,
          },
        },
        brandingId: branding.id,
        brandingInput: {
          displayName: formData.displayName,
        },
      },
    });
  }

  const productTier = useWatch<TenantUpdateFormShape>({
    control,
    name: 'tier',
  });

  const formUsageBucket = useWatch<TenantUpdateFormShape>({
    control,
    name: 'usageBucket',
  });

  const brandingId = branding?.id;
  const handleDemotionWarningClose = useCallback(
    (reason: 'confirm' | 'cancel') => {
      setShowDemotionWarning(false);

      if (reason === 'confirm' && pendingFormData && brandingId && tenantId) {
        void updateTenant({
          variables: {
            tenantInput: {
              id: tenantId,
              update: {
                usageBucket: pendingFormData.usageBucket,
                kind: pendingFormData.productionLevel,
                addonAssetProviderIntegrationEnabled:
                  pendingFormData.addonAssetProviderIntegrationEnabled,
                productTier: pendingFormData.tier,
                trialEndDate: pendingFormData.trialEndDate,
                clearTrialEndDate: pendingFormData.trialEndDate === null,
              },
            },
            brandingId,
            brandingInput: {
              displayName: pendingFormData.displayName,
            },
          },
        });
      }

      setPendingFormData(null);
    },
    [brandingId, pendingFormData, tenantId, updateTenant]
  );

  return (
    <>
      <FormProvider {...formMethods}>
        <Stack
          spacing={2}
          noValidate
          component="form"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Alert severity="info-high">
            REMINDER: You are updating the settings for &quot;{tenantSubdomain}
            &quot; in the {environment.toUpperCase()} environment.
          </Alert>
          <Box display="flex" gap={2}>
            <Box flex={1}>
              <FormAwareTextInput<TenantUpdateFormShape>
                label="Organization display name"
                helpText="To be used in places like proposals and invitation emails"
                fieldName={
                  'displayName' as const satisfies TenantUpdateFormField
                }
                required
                disabled={isSubmitting}
                control={control}
              />
            </Box>
            <Box flex={1}>
              <FormAwareSelectInput<TenantUpdateFormShape>
                label="Usage bucket"
                fieldName={
                  'usageBucket' as const satisfies TenantUpdateFormField
                }
                required
                disabled={isSubmitting}
                control={control}
                options={usageBucketOptions}
              />
            </Box>
          </Box>
          <Box sx={{ maxWidth: 350 }}>
            <FormAwareButtonGroup
              control={control}
              label="Production level"
              helpText={
                "For security reasons, tenants can't be downgraded from production."
              }
              fieldName={
                'productionLevel' as const satisfies TenantUpdateFormField
              }
              disabled={kind === TenantKind.Production || isSubmitting}
              options={[
                {
                  display: 'Non-production',
                  value: TenantKind.NonProduction,
                },
                {
                  display: 'Production',
                  value: TenantKind.Production,
                },
              ]}
            />
          </Box>
          <FormAwareSelectInput<TenantUpdateFormShape>
            label="Product tier"
            fieldName={'tier' as const satisfies TenantUpdateFormField}
            required
            disabled={isSubmitting}
            control={control}
            options={[
              {
                display: 'Go',
                value: TenantProductTier.Go,
              },
              {
                display: 'Pro',
                value: TenantProductTier.Pro,
              },
              {
                display: 'Enterprise',
                value: TenantProductTier.Enterprise,
              },
            ]}
          />
          {formUsageBucket === TenantUsageBucket.UnpaidTrial && (
            <FormAwareDatePickerInput<TenantUpdateFormShape>
              label="Trial end date"
              helpText="The date when the tenant's trial period ends"
              fieldName={
                'trialEndDate' as const satisfies TenantUpdateFormField
              }
              disabled={isSubmitting}
              control={control}
            />
          )}
          {productTier === TenantProductTier.Go && (
            <FormAwareSelectInput<TenantUpdateFormShape>
              label="Asset integration add-on"
              fieldName={
                'addonAssetProviderIntegrationEnabled' as const satisfies TenantUpdateFormField
              }
              disabled={isSubmitting}
              control={control}
              helpText="Only applicable to Go tier; included by default for other tiers"
              options={[
                {
                  display: 'Disabled',
                  value: false,
                },
                {
                  display: 'Enabled',
                  value: true,
                },
              ]}
            />
          )}
          <Button
            disabled={isSubmitting}
            variant="primary"
            size="sm"
            type="submit"
          >
            Update tenant
          </Button>
        </Stack>
      </FormProvider>

      <ConfirmationModal
        isOpen={showDemotionWarning}
        heading="Warning: Demoting Tenant from Pro to Go"
        confirmText="Proceed"
        cancelText="Cancel"
        onClose={handleDemotionWarningClose}
        dialogContentProps={{ sx: { maxWidth: 500 } }}
      >
        <Typography variant="body-s">
          Demoting this tenant from Pro to Go tier will make irreversible
          changes to the underlying households. Are you sure you want to
          proceed?
        </Typography>
      </ConfirmationModal>
    </>
  );
}
